/**
 * The executive screen (REQ-021 AC-003).
 *
 * At most ten figures, and which ten is not decided here: a definition claims
 * an `executive` position or it does not, and this page renders the ones that
 * do, in that order. The limit is enforced where the definitions live, which is
 * the only place it can be enforced against the failure it guards — a dashboard
 * that grows one reasonable figure at a time until nobody reads it.
 *
 * Nothing on this page invents a number, and nothing hides the absence of one.
 * Both of those are `MetricScreen`'s doing now: this page is that screen with a
 * selection, a heading and the alert strip, and the per-system screens are the
 * same screen with a different selection. What stays here is what is true of
 * this screen alone — the ten, and the alerts above them.
 */
import { useQuery } from "@tanstack/react-query";
import { BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";
import apiClient from "@/services/api/apiClient";
import AlertStrip from "./AlertStrip";
import type { AlertsMeta, AlertsPayload } from "./alerts";
import MetricScreen from "./MetricScreen";
import {
  executiveOrder,
  METRIC_GROUPS,
  groupLabel,
  groupPath,
} from "./metrics";

export default function ExecutiveDashboardPage() {
  /**
   * The alerts are their own query rather than part of the metrics one: a
   * dashboard whose figures load and whose alerts do not must still say the
   * alerts did not load, and a single query would make that one silence.
   */
  const alerts = useQuery({
    queryKey: ["dashboard", "alerts"],
    queryFn: async () => {
      const response = await apiClient.get<{
        data: AlertsPayload;
        meta: AlertsMeta;
      }>("/staff/dashboard/alerts");
      return response.data;
    },
    retry: false,
    refetchInterval: 5 * 60_000,
  });

  return (
    <MetricScreen
      title="Executive"
      icon={BarChart3}
      // The ten, and nothing else. `executiveOrder` reads the position each
      // definition claims; a metric without one belongs to its system screen.
      select={executiveOrder}
      emptyMessage="No metric claims a place on this screen. That is a gap in the metric registry, not an empty company."
      intro={
        <>
          The figures the company acts on. Every one of them has a written
          definition, a source and an owner —{" "}
          <Link
            to="/admin/dashboard/definitions"
            className="font-medium text-sky-700 hover:text-sky-900"
          >
            see the definitions
          </Link>
          {/* Not "these ten": the cap is ten, but only the positions actually
              claimed are drawn, and stating a count the screen does not show
              is the sort of small untruth this dashboard exists to avoid. */}
          . These are a selection, not the measurement: every metric, including
          the ones that reach no front page, is on{" "}
          {/* Named screens rather than one "more metrics" link. A reader
              looking for a number knows which system it belongs to, and a
              single link would make them open all seven to find out. */}
          {METRIC_GROUPS.map((group, index) => (
            <span key={group}>
              {index > 0 &&
                (index === METRIC_GROUPS.length - 1 ? " and " : ", ")}
              <Link
                to={groupPath(group)}
                className="font-medium text-sky-700 hover:text-sky-900"
              >
                {groupLabel(group).toLowerCase()}
              </Link>
            </span>
          ))}
          .
        </>
      }
    >
      {/* Above the figures: what needs a person comes before what a person
          might read. Only on this screen — a strip repeated on all eight would
          be read on none of them. */}
      <AlertStrip
        alerts={alerts.data?.data.alerts ?? []}
        meta={alerts.data?.meta}
        isLoading={alerts.isLoading}
        isError={alerts.isError}
      />
    </MetricScreen>
  );
}
